/**
 * Kalenderwochen nach ISO 8601 — die EINZIGE Stelle dafür (AC-109).
 *
 * Öffentlich wird nur mit reinen Kalenderdaten `'YYYY-MM-DD'` gerechnet. Ein
 * Zeitpunkt wird genau einmal, in `calendarDateIn`, in einer ausdrücklich
 * genannten Zeitzone auf ein Kalenderdatum reduziert. Danach spielt die
 * Zeitzone des Prozesses keine Rolle mehr: intern zählen ganze Tage seit
 * 1970-01-01 (UTC). Entscheidungen: AC-109_Entscheidungsprotokoll.md.
 *
 * Dieses Modul importiert nichts, damit `common.ts` es ohne Zyklus
 * weiterexportieren kann und die Oberfläche es über den Unterpfad
 * `@asas/domain/iso-week` ohne Zod laden kann. Die Zod-Schemata dazu stehen in
 * `iso-week-schemas.ts` (beim Einmergen von AC-103/AC-104 abgetrennt; Nachtrag
 * zu E1 im AC-109-Entscheidungsprotokoll).
 */

/** Reines Kalenderdatum 'YYYY-MM-DD', keine Uhrzeit, keine Zeitzone. */
export type CalendarDate = string;

export interface IsoWeek {
  readonly isoYear: number;
  readonly isoWeek: number;
}

/** Kanonischer Schlüssel, z. B. '2026-W05'. Nicht die Austauschform (E3). */
export type IsoWeekKey = `${number}-W${string}`;

/** Fachliche Zeitzone als Vorgabe; die Funktionen nehmen sie als Parameter (E6). */
export const FACHLICHE_ZEITZONE = 'Europe/Berlin';

export interface PlanningWeek extends IsoWeek {
  readonly key: IsoWeekKey;
  readonly monday: CalendarDate;
}

export type HorizonLength = { readonly years: number } | { readonly weeks: number };

export const DEFAULT_HORIZON: HorizonLength = { years: 4 };
export const MAX_HORIZON_YEARS = 6;
/** Die größte Wochenzahl in sechs aufeinanderfolgenden ISO-Jahren (E5). */
export const MAX_HORIZON_WEEKS = 314;

export type IsoWeekErrorCode =
  | 'INVALID_DATE'
  | 'INVALID_TIME_ZONE'
  | 'INVALID_WEEK'
  | 'UNPARSEABLE'
  | 'OUT_OF_RANGE'
  | 'HORIZON_OUT_OF_RANGE';

export class IsoWeekError extends Error {
  override readonly name = 'IsoWeekError';

  constructor(
    readonly code: IsoWeekErrorCode,
    message: string,
  ) {
    super(message);
  }
}

/** Wertebereich der ISO-Jahre; auch für `iso-week-schemas.ts`. */
export const ISO_YEAR_MIN = 1;
export const ISO_YEAR_MAX = 9999;
const MIN_YEAR = ISO_YEAR_MIN;
const MAX_YEAR = ISO_YEAR_MAX;
const MS_PER_DAY = 86_400_000;
/** 1970-01-05 war ein Montag; Bezugspunkt für die fortlaufende Wochennummer. */
const REFERENCE_MONDAY = 4;

function isYearInRange(year: number): boolean {
  return Number.isInteger(year) && year >= MIN_YEAR && year <= MAX_YEAR;
}

/** Tage seit 1970-01-01. `setUTCFullYear`, weil `Date.UTC` Jahre unter 100 verschiebt. */
function toDayNumber(year: number, month: number, day: number): number {
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return Math.round(date.getTime() / MS_PER_DAY);
}

function fromDayNumber(dayNumber: number): { year: number; month: number; day: number } {
  const date = new Date(dayNumber * MS_PER_DAY);
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
}

/** ISO-Wochentag: 1 = Montag … 7 = Sonntag. Tag 0 (1970-01-01) war ein Donnerstag. */
function isoWeekday(dayNumber: number): number {
  return ((((dayNumber + 3) % 7) + 7) % 7) + 1;
}

function formatDayNumber(dayNumber: number): CalendarDate {
  const { year, month, day } = fromDayNumber(dayNumber);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseCalendarDate(date: CalendarDate): number {
  const match = typeof date === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(date) : null;
  if (!match) throw new IsoWeekError('INVALID_DATE', `Kein Kalenderdatum 'YYYY-MM-DD': ${date}`);
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const dayNumber = toDayNumber(year, month, day);
  const roundTrip = fromDayNumber(dayNumber);
  if (
    !isYearInRange(year) ||
    roundTrip.year !== year ||
    roundTrip.month !== month ||
    roundTrip.day !== day
  ) {
    throw new IsoWeekError('INVALID_DATE', `Kein gültiges Kalenderdatum: ${date}`);
  }
  return dayNumber;
}

/** Montag der KW 1: die Woche, die den 4. Januar enthält. */
function mondayOfWeekOne(isoYear: number): number {
  const januaryFourth = toDayNumber(isoYear, 1, 4);
  return januaryFourth - isoWeekday(januaryFourth) + 1;
}

/** Die Woche eines Tages ist die Woche ihres Donnerstags. */
function weekOfDayNumber(dayNumber: number): IsoWeek {
  const thursday = dayNumber - isoWeekday(dayNumber) + 4;
  const isoYear = fromDayNumber(thursday).year;
  if (!isYearInRange(isoYear)) {
    throw new IsoWeekError('OUT_OF_RANGE', `Woche außerhalb 0001-W01 … 9999-W52`);
  }
  return { isoYear, isoWeek: Math.floor((thursday - mondayOfWeekOne(isoYear)) / 7) + 1 };
}

function assertValidWeek(week: IsoWeek): void {
  if (!isValidIsoWeek(week)) {
    throw new IsoWeekError(
      'INVALID_WEEK',
      `Keine gültige ISO-Woche: ${String(week?.isoYear)}-W${String(week?.isoWeek)}`,
    );
  }
}

function mondayDayNumber(week: IsoWeek): number {
  assertValidWeek(week);
  return mondayOfWeekOne(week.isoYear) + (week.isoWeek - 1) * 7;
}

/** Fortlaufende Wochennummer; Grundlage für Arithmetik und Vergleich. */
function weekIndex(week: IsoWeek): number {
  return (mondayDayNumber(week) - REFERENCE_MONDAY) / 7;
}

/** Ein ISO-Jahr hat 53 Wochen, wenn es mit Donnerstag beginnt, im Schaltjahr auch mit Mittwoch. */
export function weeksInIsoYear(isoYear: number): 52 | 53 {
  if (!isYearInRange(isoYear)) {
    throw new IsoWeekError('INVALID_WEEK', `ISO-Jahr außerhalb 1 … 9999: ${String(isoYear)}`);
  }
  const weekday = isoWeekday(toDayNumber(isoYear, 1, 1));
  const leapYear = isoYear % 4 === 0 && (isoYear % 100 !== 0 || isoYear % 400 === 0);
  return weekday === 4 || (weekday === 3 && leapYear) ? 53 : 52;
}

/** Prüft, ohne zu werfen. Signatur unverändert seit AC-015 (vormals in common.ts). */
export function isValidIsoWeek({ isoYear, isoWeek }: IsoWeek): boolean {
  if (!isYearInRange(isoYear)) return false;
  if (!Number.isInteger(isoWeek) || isoWeek < 1) return false;
  return isoWeek <= weeksInIsoYear(isoYear);
}

/** Einzige Stelle Zeitpunkt → Kalenderdatum (E6). */
export function calendarDateIn(instant: Date, timeZone: string): CalendarDate {
  if (!(instant instanceof Date) || Number.isNaN(instant.getTime())) {
    throw new IsoWeekError('INVALID_DATE', 'Kein gültiger Zeitpunkt');
  }
  // Ohne Zeitzone fiele Intl still auf die des Prozesses zurück; reine
  // Versätze wie '+02:00' kennen keine Sommerzeit. Beides ist hier ein Fehler.
  if (typeof timeZone !== 'string' || timeZone === '' || /^[+-]/.test(timeZone)) {
    throw new IsoWeekError('INVALID_TIME_ZONE', `Keine IANA-Zeitzone: ${String(timeZone)}`);
  }
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      era: 'short',
    });
  } catch {
    throw new IsoWeekError('INVALID_TIME_ZONE', `Keine IANA-Zeitzone: ${timeZone}`);
  }
  const parts = formatter.formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';
  // Vor Christus liefert `year` den Betrag; das Zeitalter schließt das aus.
  if (part('era') !== 'AD') {
    throw new IsoWeekError('INVALID_DATE', 'Zeitpunkt außerhalb 0001 … 9999');
  }
  const date = `${part('year').padStart(4, '0')}-${part('month')}-${part('day')}`;
  parseCalendarDate(date);
  return date;
}

export function isoWeekOf(date: CalendarDate): IsoWeek {
  return weekOfDayNumber(parseCalendarDate(date));
}

export function currentIsoWeek(now: Date, timeZone: string): IsoWeek {
  return isoWeekOf(calendarDateIn(now, timeZone));
}

export function mondayOf(week: IsoWeek): CalendarDate {
  return formatDayNumber(mondayDayNumber(week));
}

/**
 * Der Kalendermonat einer Woche ist der ihres Donnerstags (AC-410, E2) — nach
 * derselben Regel, nach der eine Woche ihr ISO-Jahr bekommt. Damit gehört jede
 * Woche genau einem Monat, und `year` ist immer ihr ISO-Jahr: KW 1/2030 (Montag
 * 31.12.2029) liegt im Januar 2030, KW 53/2026 im Dezember 2026.
 */
export function monthOfIsoWeek(week: IsoWeek): { readonly year: number; readonly month: number } {
  const { year, month } = fromDayNumber(mondayDayNumber(week) + 3);
  return { year, month };
}

export function addWeeks(week: IsoWeek, n: number): IsoWeek {
  if (!Number.isInteger(n)) {
    throw new IsoWeekError('OUT_OF_RANGE', `Wochenanzahl ist nicht ganzzahlig: ${String(n)}`);
  }
  return weekOfDayNumber(REFERENCE_MONDAY + (weekIndex(week) + n) * 7);
}

/** `to` minus `from` in Wochen. */
export function weeksBetween(from: IsoWeek, to: IsoWeek): number {
  return weekIndex(to) - weekIndex(from);
}

export function compareIsoWeeks(a: IsoWeek, b: IsoWeek): -1 | 0 | 1 {
  return Math.sign(weeksBetween(b, a)) as -1 | 0 | 1;
}

export function formatIsoWeekKey(week: IsoWeek): IsoWeekKey {
  assertValidWeek(week);
  return `${String(week.isoYear).padStart(4, '0')}-W${String(week.isoWeek).padStart(2, '0')}` as IsoWeekKey;
}

const KEY_FORM = /^(\d{4})-?W(\d{1,2})$/i;
const KW_FORM = /^KW\s*(\d{1,2})\s*\/\s*(\d{4})$/i;

/**
 * Akzeptiert ohne Rücksicht auf Groß-/Kleinschreibung und Leerraum außen:
 * `YYYY-Www`, `YYYY-Ww`, `YYYYWww`, `KWw/YYYY`, `KW ww / YYYY`.
 */
export function parseIsoWeek(input: string): IsoWeek {
  const text = typeof input === 'string' ? input.trim() : '';
  const keyMatch = KEY_FORM.exec(text);
  const kwMatch = keyMatch ? null : KW_FORM.exec(text);
  let week: IsoWeek;
  if (keyMatch) week = { isoYear: Number(keyMatch[1]), isoWeek: Number(keyMatch[2]) };
  else if (kwMatch) week = { isoYear: Number(kwMatch[2]), isoWeek: Number(kwMatch[1]) };
  else throw new IsoWeekError('UNPARSEABLE', `Keine Kalenderwoche erkannt: ${String(input)}`);
  assertValidWeek(week);
  return week;
}

export function toPlanningWeek(week: IsoWeek): PlanningWeek {
  return {
    isoYear: week.isoYear,
    isoWeek: week.isoWeek,
    key: formatIsoWeekKey(week),
    monday: mondayOf(week),
  };
}

function horizonWeekCount(start: IsoWeek, length: HorizonLength): number {
  if ('years' in length) {
    const { years } = length;
    if (!Number.isInteger(years) || years < 1 || years > MAX_HORIZON_YEARS) {
      throw new IsoWeekError(
        'HORIZON_OUT_OF_RANGE',
        `Jahre außerhalb 1 … ${MAX_HORIZON_YEARS}: ${String(years)}`,
      );
    }
    const targetYear = start.isoYear + years;
    if (targetYear > MAX_YEAR) throw new IsoWeekError('OUT_OF_RANGE', 'Horizont endet nach 9999');
    // Endregel E5: gleicher Schlüssel `years` später, sonst KW 1 des Folgejahres.
    const endIndex =
      start.isoWeek <= weeksInIsoYear(targetYear)
        ? weekIndex({ isoYear: targetYear, isoWeek: start.isoWeek })
        : weekIndex({ isoYear: targetYear, isoWeek: 1 }) + weeksInIsoYear(targetYear);
    return endIndex - weekIndex(start);
  }
  const { weeks } = length;
  if (!Number.isInteger(weeks) || weeks < 1 || weeks > MAX_HORIZON_WEEKS) {
    throw new IsoWeekError(
      'HORIZON_OUT_OF_RANGE',
      `Wochen außerhalb 1 … ${MAX_HORIZON_WEEKS}: ${String(weeks)}`,
    );
  }
  return weeks;
}

/**
 * Lückenlose Liste der Kalenderwochen ab `start` (E5). Nur Kalender: wie viele
 * Wochen Daten geladen werden dürfen, regelt ADR-07 (höchstens 26), nicht diese
 * Funktion.
 */
export function planningHorizon(
  start: IsoWeek,
  length: HorizonLength = DEFAULT_HORIZON,
): readonly PlanningWeek[] {
  assertValidWeek(start);
  const count = horizonWeekCount(start, length);
  const first = weekIndex(start);
  const weeks: PlanningWeek[] = [];
  for (let offset = 0; offset < count; offset += 1) {
    weeks.push(toPlanningWeek(weekOfDayNumber(REFERENCE_MONDAY + (first + offset) * 7)));
  }
  return weeks;
}
